/**
 * PIN setup for OAuth users who signed in without a login PIN.
 */
import React, { useState } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Shield, Loader2 } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { API_URL } from '../config';
import { AppBrandLogo } from '../components/AppBrandLogo';
import AuthSplitShell from '../components/marketing/AuthSplitShell';
import { IMAGES } from '../lib/marketingImages';

export default function SetupPinPage() {
  const [pin, setPin] = useState('');
  const [confirmPin, setConfirmPin] = useState('');
  const [loading, setLoading] = useState(false);
  const { user, refreshUser, loading: authLoading } = useAuth();
  const navigate = useNavigate();

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (!user.needs_pin_setup) {
    return <Navigate to="/" replace />;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (pin.length < 4 || pin.length > 6) {
      toast.error('PIN must be 4–6 digits');
      return;
    }
    if (pin !== confirmPin) {
      toast.error('PINs do not match');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('auth_token');
      await axios.post(
        `${API_URL}/auth/set-pin`,
        { pin },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      await refreshUser?.();
      toast.success('PIN set — you can now pay and manage your account securely');
      navigate(user?.is_admin ? '/admin' : '/');
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Could not set PIN');
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthSplitShell
      image={IMAGES.heroHome}
      kicker="Almost there"
      headline="Set a PIN so only you can pay the household bills."
      caption="Four to six digits. Same teal, same peace of mind."
    >
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="w-full">
        <div className="text-center mb-8 lg:hidden">
          <AppBrandLogo banner className="mb-4" />
        </div>

        <div className="bg-card border border-border/50 rounded-2xl p-8 shadow-xl">
          <h2 className="text-xl font-semibold mb-2 text-center">Set your PIN</h2>
          <p className="text-sm text-muted-foreground text-center mb-6">
            Payments, wallet transfers, and account deletion require a 4–6 digit PIN.
          </p>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="block text-sm font-medium mb-2 text-muted-foreground">New PIN</label>
              <div className="relative">
                <Shield className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                <input
                  type="password"
                  inputMode="numeric"
                  maxLength={6}
                  value={pin}
                  onChange={(e) => setPin(e.target.value.replace(/\D/g, ''))}
                  placeholder="4–6 digits"
                  className="w-full pl-12 pr-4 py-4 bg-input/50 border border-border rounded-xl"
                  data-testid="setup-pin-input"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium mb-2 text-muted-foreground">Confirm PIN</label>
              <div className="relative">
                <Shield className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                <input
                  type="password"
                  inputMode="numeric"
                  maxLength={6}
                  value={confirmPin}
                  onChange={(e) => setConfirmPin(e.target.value.replace(/\D/g, ''))}
                  placeholder="Repeat PIN"
                  className="w-full pl-12 pr-4 py-4 bg-input/50 border border-border rounded-xl"
                  data-testid="setup-pin-confirm"
                />
              </div>
            </div>
            <button
              type="submit"
              disabled={loading}
              className="w-full py-4 bg-primary text-primary-foreground rounded-xl font-semibold hover:bg-primary/90 disabled:opacity-50 flex items-center justify-center gap-2"
              data-testid="setup-pin-submit"
            >
              {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Save PIN & Continue'}
            </button>
          </form>
        </div>
      </motion.div>
    </AuthSplitShell>
  );
}
